import type { ReactElement } from "react";

type PatternRenderer = (id: string) => ReactElement;

const genreColors: Record<string, string> = {
  adventure: "rgba(251,191,36,0.12)",
  fantasy: "rgba(167,139,250,0.15)",
  horror: "rgba(71,85,105,0.22)",
  mystery: "rgba(99,102,241,0.13)",
  romance: "rgba(251,113,133,0.15)",
  comedy: "rgba(250,204,21,0.15)",
  "sci-fi": "rgba(34,211,238,0.12)",
  thriller: "rgba(51,65,85,0.2)",
  historical: "rgba(180,151,109,0.15)",
  literary: "rgba(161,161,170,0.14)",
  paranormal: "rgba(192,132,252,0.14)",
  sport: "rgba(249,115,22,0.13)",
  western: "rgba(217,119,6,0.13)",
  realistic: "rgba(120,113,108,0.14)",
  kids: "rgba(56,189,248,0.14)",
  survival: "rgba(132,204,22,0.13)",
  strategic: "rgba(100,116,139,0.16)",
  contemporary: "rgba(45,212,191,0.12)",
  "slice of life": "rgba(253,186,116,0.14)",
};

function getGenreColor(genreName?: string): string | undefined {
  if (!genreName) return undefined;
  const key = genreName.toLowerCase();
  for (const [pattern, color] of Object.entries(genreColors)) {
    if (key.includes(pattern)) return color;
  }
  return undefined;
}

const patterns: Record<string, PatternRenderer> = {
  adventure: (id) => (
    <pattern id={id} width="24" height="24" patternUnits="userSpaceOnUse">
      <path
        d="M12 2 L22 12 L12 22 L2 12Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  fantasy: (id) => (
    <pattern id={id} width="28" height="28" patternUnits="userSpaceOnUse">
      <path
        d="M14 2 L16.5 10 L25 10 L18 15.5 L20.5 24 L14 18.5 L7.5 24 L10 15.5 L3 10 L11.5 10Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  horror: (id) => (
    <pattern id={id} width="20" height="20" patternUnits="userSpaceOnUse">
      <path
        d="M4 4 L16 16 M16 4 L4 16"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  mystery: (id) => (
    <pattern id={id} width="24" height="24" patternUnits="userSpaceOnUse">
      <circle
        cx="12"
        cy="12"
        r="8"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </pattern>
  ),
  romance: (id) => (
    <pattern id={id} width="24" height="22" patternUnits="userSpaceOnUse">
      <path
        d="M12 20 C12 20 2 13 2 7.5 C2 4 5 2 8 4 C10 5.5 11.5 7 12 8 C12.5 7 14 5.5 16 4 C19 2 22 4 22 7.5 C22 13 12 20 12 20Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  comedy: (id) => (
    <pattern id={id} width="20" height="20" patternUnits="userSpaceOnUse">
      <circle
        cx="10"
        cy="10"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <circle cx="7" cy="8" r="1" fill="currentColor" />
      <circle cx="13" cy="8" r="1" fill="currentColor" />
      <path
        d="M6 12 Q10 16 14 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  "sci-fi": (id) => (
    <pattern id={id} width="26" height="30" patternUnits="userSpaceOnUse">
      <path
        d="M13 0 L24 7.5 L24 22.5 L13 30 L2 22.5 L2 7.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  thriller: (id) => (
    <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
      <path
        d="M0 16 L16 0 M-2 2 L2 -2 M14 18 L18 14"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  historical: (id) => (
    <pattern id={id} width="24" height="24" patternUnits="userSpaceOnUse">
      <path
        d="M4 20 L4 4 L20 4 L20 20 M8 20 L8 8 L16 8 L16 16 M12 16 L12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  literary: (id) => (
    <pattern id={id} width="26" height="22" patternUnits="userSpaceOnUse">
      <path
        d="M13 6 C10 3.5 6 3.5 3 5 L3 18 C6 16.5 10 16.5 13 19 C16 16.5 20 16.5 23 18 L23 5 C20 3.5 16 3.5 13 6Z M13 6 L13 19"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  paranormal: (id) => (
    <pattern id={id} width="24" height="24" patternUnits="userSpaceOnUse">
      <path
        d="M14 3 A9 9 0 1 0 14 21 A7 7 0 1 1 14 3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <circle cx="20" cy="6" r="0.8" fill="currentColor" />
    </pattern>
  ),
  sport: (id) => (
    <pattern id={id} width="22" height="22" patternUnits="userSpaceOnUse">
      <circle
        cx="11"
        cy="11"
        r="8"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <path
        d="M4 6 Q11 11 4 16 M18 6 Q11 11 18 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  western: (id) => (
    <pattern id={id} width="22" height="24" patternUnits="userSpaceOnUse">
      <path
        d="M6 21 L6 12 A5 5 0 0 1 16 12 L16 21"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <circle cx="6" cy="19" r="0.7" fill="currentColor" />
      <circle cx="16" cy="19" r="0.7" fill="currentColor" />
    </pattern>
  ),
  realistic: (id) => (
    <pattern id={id} width="20" height="20" patternUnits="userSpaceOnUse">
      <path d="M0 0 L20 0 M0 0 L0 20" stroke="currentColor" strokeWidth="0.5" />
    </pattern>
  ),
  kids: (id) => (
    <pattern id={id} width="20" height="26" patternUnits="userSpaceOnUse">
      <circle
        cx="10"
        cy="9"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <path
        d="M10 15 Q8 20 10 25"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  survival: (id) => (
    <pattern id={id} width="24" height="22" patternUnits="userSpaceOnUse">
      <path
        d="M12 3 L21 19 L3 19Z M12 8 L9 19 M12 8 L15 19"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  strategic: (id) => (
    <pattern id={id} width="20" height="20" patternUnits="userSpaceOnUse">
      <rect
        x="0"
        y="0"
        width="10"
        height="10"
        fill="currentColor"
        fillOpacity="0.45"
      />
      <rect
        x="10"
        y="10"
        width="10"
        height="10"
        fill="currentColor"
        fillOpacity="0.45"
      />
    </pattern>
  ),
  contemporary: (id) => (
    <pattern id={id} width="24" height="24" patternUnits="userSpaceOnUse">
      <rect
        x="3"
        y="3"
        width="12"
        height="12"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <circle
        cx="16"
        cy="16"
        r="6"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
  "slice of life": (id) => (
    <pattern id={id} width="24" height="22" patternUnits="userSpaceOnUse">
      <path
        d="M5 8 L5 15 A4 4 0 0 0 13 15 L13 8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <path
        d="M13 10 L16 10 A2.5 2.5 0 0 1 16 15 L13 15"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
      <path
        d="M7 5 Q8 3 7 1 M11 5 Q12 3 11 1"
        fill="none"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </pattern>
  ),
};

const defaultPattern: PatternRenderer = (id) => (
  <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse">
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
  </pattern>
);

function getPattern(genreName?: string): PatternRenderer {
  if (!genreName) return defaultPattern;
  const key = genreName.toLowerCase();
  for (const [pattern, renderer] of Object.entries(patterns)) {
    if (key.includes(pattern)) return renderer;
  }
  return defaultPattern;
}

let counter = 0;

export function GenrePattern({ genre }: { genre?: string }) {
  const patternId = `gp-${genre ?? "default"}-${counter++}`;
  const renderer = getPattern(genre);
  const color = getGenreColor(genre);

  return (
    <svg
      className="absolute inset-0 w-full h-full text-muted-foreground/10"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>{renderer(patternId)}</defs>
      {color && <rect width="100%" height="100%" fill={color} />}
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}
